// src/api/billingService.js
import apiClient from './axiosConfig'; // Your configured axios instance
import { toast } from 'react-toastify'; // Optional: for direct error toasts from service

/**
 * Standardized API error handler.
 * @param {Error} error - The error object, typically from Axios.
 * @param {string} operation - A string describing the operation that failed.
 * @returns {{isError: boolean, message: string, status: number|null, data: any|null, originalError: Error}}
 */
const handleApiError = (error, operation) => {
  console.error(`Error during ${operation}:`, error);
  let message = `Failed to ${operation}. Please try again.`;
  let status = null;
  let errorData = null;

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    status = error.response.status;
    errorData = error.response.data;
    if (error.response.data?.detail) {
      if (Array.isArray(error.response.data.detail)) { // Handle Pydantic validation errors
        message = error.response.data.detail.map(err => `${err.loc.join('.')} - ${err.msg}`).join('; ');
      } else {
        message = error.response.data.detail;
      }
    } else if (typeof error.response.data === 'string' && error.response.data.length < 200 && !error.response.data.trim().startsWith('<')) {
      message = error.response.data; // Plain string error from backend
    } else {
      message = `Server error (${status}) during ${operation}.`;
    }
  } else if (error.request) {
    // The request was made but no response was received
    message = `No response from server during ${operation}. Check your network.`;
  } else {
    // Something happened in setting up the request that triggered an Error
    message = error.message || `An unexpected error occurred during ${operation}.`;
  }

  // Optional: If you want the service itself to show a toast.
  // Generally, it's better to let the component calling the service handle UI feedback.
  // if (status !== 401 && status !== 403) { // Avoid toasting for auth errors handled by interceptors/routing
  //   toast.error(message, {toastId: `billing-service-${operation}-${Date.now()}`});
  // }

  return { isError: true, message, status, data: errorData, originalError: error };
};


export const billingService = {
  /**
   * Fetches the current user's credit balance and active subscription status.
   * GET /api/v1/users/me/credits
   * @returns {Promise<object|{isError: boolean, message: string}>} UserCreditsAndSubscriptionResponse or error object.
   */
  async getAccountStatus() {
    try {
      const { data } = await apiClient.get('/users/me/credits');
      return data; // Expected: UserCreditsAndSubscriptionResponse
    } catch (error) {
      return handleApiError(error, 'fetching account status');
    }
  },

  /**
   * Fetches all available subscription plans and one-time credit purchase options.
   * GET /api/v1/billing/plans
   * @returns {Promise<object|{isError: boolean, message: string}>} AvailablePlansResponse or error object.
   */
  async getAvailablePlans() {
    try {
      const { data } = await apiClient.get('/billing/plans');
      return data; // Expected: AvailablePlansResponse
    } catch (error)      {
      return handleApiError(error, 'fetching available plans');
    }
  },

/**
   * Creates a Stripe Checkout session for purchasing credits or subscribing to a plan.
   * POST /api/v1/billing/create-checkout-session  <-- CORRECTED ENDPOINT
   * @param {object} payload - { price_id: string, quantity?: number, success_url: string, cancel_url: string }
   * @returns {Promise<object|{isError: boolean, message: string}>} CreateCheckoutSessionResponse or error object.
   */
async createCheckoutSession(payload) {
    try {
      const { price_id, quantity, success_url, cancel_url } = payload;
      const requestBody = { price_id, success_url, cancel_url };
      // Backend default quantity to 1, so only include if explicitly provided by FE
      if (quantity !== undefined && quantity !== null) { // Check for null as well, as quantity might be 0 for some rare cases.
        requestBody.quantity = quantity;
      }

      // *** CORRECTED ENDPOINT PATH ***
      const { data } = await apiClient.post('/billing/create-checkout-session', requestBody);
      return data; // Expected: { checkout_url: string, session_id: string }
    } catch (error) {
      return handleApiError(error, 'creating Stripe checkout session');
    }
  },
  /**
   * Creates a Stripe Customer Portal session for the user to manage their subscription.
   * POST /api/v1/billing/create-customer-portal-session
   * @returns {Promise<object|{isError: boolean, message: string}>} CreateCustomerPortalSessionResponse or error object.
   */
  async createCustomerPortalSession() {
    try {
      // Backend typically uses the authenticated user's ID, no payload needed from FE for this usually.
      // If your backend needs a return_url:
      // const payload = { return_url: `${window.location.origin}/billing` };
      // const { data } = await apiClient.post('/billing/create-customer-portal-session', payload);
      const { data } = await apiClient.post('/billing/create-customer-portal-session');
      return data; // Expected: CreateCustomerPortalSessionResponse { portal_url: string }
    } catch (error) {
      return handleApiError(error, 'creating Stripe customer portal session');
    }
  },

  /**
   * Fetches the credit transaction history for the authenticated user.
   * GET /api/v1/users/me/credit-transactions
   * @param {number} limit - Number of transactions per page.
   * @param {number} offset - Offset for pagination.
   * @returns {Promise<object|{isError: boolean, message: string}>} CreditTransactionListResponse or error object.
   */
  async getCreditTransactions(limit = 10, offset = 0) {
    try {
      const params = new URLSearchParams();
      params.append('limit', String(limit));
      params.append('offset', String(offset));

      const { data } = await apiClient.get('/users/me/credit-transactions', { params });
      return data; // Expected: CreditTransactionListResponse { items: [], total: number }
    } catch (error) {
      return handleApiError(error, 'fetching credit transactions');
    }
  },

  /**
   * Fetches an aggregated summary of the user's LLM usage.
   * GET /api/v1/users/me/usage-summary
   * @param {string} period - e.g., "today", "last_7_days", "current_month", "last_30_days".
   * @returns {Promise<object|{isError: boolean, message: string}>} LLMUsageSummaryResponse or error object.
   */
  async getUsageSummary(period = "current_month") {
    try {
      const params = new URLSearchParams();
      params.append('period', period);

      const { data } = await apiClient.get('/users/me/usage-summary', { params });
      return data; // Expected: LLMUsageSummaryResponse
    } catch (error) {
      return handleApiError(error, 'fetching usage summary');
    }
  },
};